import React from 'react'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import EditOutlinedIcon from 'components/material-symbol/icons/Edit'
import { makeStyles } from '@material-ui/core/styles'
import EditAvailabilityFormDialog from '../availability-manager/edit-availability-form-dialog'
import { daysOfWeek } from '../availability-manager/constants'
import { dayNames, formatTime12 } from './utils'

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(3),
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(2),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.only('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  title: {
    fontWeight: 700,
    lineHeight: 1.3,
  },
  description: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  editButton: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    alignSelf: 'flex-start',
  },
  list: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  dayLabel: {
    fontWeight: 500,
  },
  hoursWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 3,
    backgroundColor: '#E3F2FD',
    border: '1px solid #90CAF9',
    boxSizing: 'border-box',
    display: 'inline-block',
    flexShrink: 0,
  },
  hoursLabel: {
    color: theme.palette.text.primary,
    fontVariantNumeric: 'tabular-nums',
  },
  unavailable: {
    color: theme.palette.text.secondary,
  },
}))

const formatTimeRange = timeSlots => {
  if (!timeSlots || timeSlots.length === 0) return null
  return timeSlots
    .map(ts => `${formatTime12(ts.startTime)} – ${formatTime12(ts.endTime)}`)
    .join(', ')
}

const aggregateByDay = timeSlots =>
  daysOfWeek.reduce((acc, d) => {
    acc[d] = (timeSlots || []).filter(ts => ts.day === d)
    return acc
  }, {})

const RecurringHoursSidebar = ({ availability, onSuccess }) => {
  const classes = useStyles()
  const byDay = aggregateByDay(availability.timeSlots)

  return (
    <Card variant="outlined" className={classes.card}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h6" className={classes.title}>
            Recurring Bookable Hours
          </Typography>
          <Typography variant="body2" className={classes.description}>
            Your weekly schedule of hours when students can book sessions with
            you.
          </Typography>
        </Box>
        <EditAvailabilityFormDialog
          trigger={
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon fontSize="small" />}
              className={classes.editButton}
            >
              Edit Weekly Hours
            </Button>
          }
          initialTimeSlots={availability.timeSlots}
          coachFacilityId={availability.coachFacility?.id}
          facilityTimezone={availability.timeZone}
          onSuccess={onSuccess}
        />
      </Box>
      <Box className={classes.list}>
        {daysOfWeek.map((day, idx) => {
          const slots = byDay[day]
          const isAvailable = slots.length > 0
          return (
            <Box key={day} className={classes.row}>
              <Typography variant="body2" className={classes.dayLabel}>
                {dayNames[idx]}
              </Typography>
              {isAvailable ? (
                <Box className={classes.hoursWrap}>
                  <span className={classes.dot} aria-hidden />
                  <Typography variant="body2" className={classes.hoursLabel}>
                    {formatTimeRange(slots)}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" className={classes.unavailable}>
                  Unavailable
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>
    </Card>
  )
}

export default RecurringHoursSidebar
